import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Badge } from '../../../components/ui/badge';
import { Loader2, MapPinOff } from 'lucide-react';
import { toast } from 'sonner';
import { adminService } from '../../../services/adminService';

const shortDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? String(value).slice(0, 10)
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// One pass over the orders with no delivery site recorded. A match is proposed
// only where the address on the order is the address of exactly one site, and
// nothing changes until it is applied.
export const LocationBacklogDialog = ({ open, onOpenChange, onApplied }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [chosen, setChosen] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await adminService.getOrderLocationBacklog();
      setData(result);
      setChosen(new Set(result.rows.filter((r) => r.proposed_location_id).map((r) => r.order_id)));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not read the backlog');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = (orderId) => setChosen((prev) => {
    const next = new Set(prev);
    if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
    return next;
  });

  const apply = async () => {
    setApplying(true);
    try {
      const result = await adminService.applyOrderLocationBacklog([...chosen]);
      toast.success(`${result.applied} order(s) now carry their delivery site`);
      await load();
      onApplied?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not apply the matches');
    } finally {
      setApplying(false);
    }
  };

  const matched = (data?.rows || []).filter((r) => r.proposed_location_id);
  const unmatched = (data?.rows || []).filter((r) => !r.proposed_location_id);
  const totals = data?.totals || {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="location-backlog-dialog">
        <DialogHeader>
          <DialogTitle>Orders with no delivery site</DialogTitle>
          <DialogDescription>
            A site is proposed only where the address on the order is the address of exactly one of
            that client&apos;s sites. Nothing changes until you apply it, and no amounts move.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
        ) : (
          <div className="space-y-4">
            <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-gray-800"
              data-testid="location-backlog-totals">
              <span className="font-medium">{totals.unmatched || 0}</span> order(s) have no site
              recorded · <span className="font-medium">{totals.with_proposal || 0}</span> can be
              matched from their address · {totals.without_proposal || 0} need editing by hand.
            </p>

            {matched.length > 0 && (
              <div className="space-y-1.5" data-testid="location-backlog-matched">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Proposed matches
                </p>
                {matched.map((row) => (
                  <label
                    key={row.order_id}
                    className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 hover:bg-amber-50/40"
                    data-testid={`location-backlog-row-${row.order_id}`}
                  >
                    <Checkbox
                      checked={chosen.has(row.order_id)}
                      onCheckedChange={() => toggle(row.order_id)}
                      data-testid={`location-backlog-check-${row.order_id}`}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="font-medium text-gray-900">{row.order_number}</span>
                        <span className="text-gray-600">{row.client_name}</span>
                        <span className="text-xs text-gray-500">{shortDate(row.delivery_date)}</span>
                        <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-[10px] text-emerald-800">
                          {row.proposed_location_name}
                        </Badge>
                      </span>
                      <span className="mt-0.5 block text-xs text-gray-500">{row.delivery_address}</span>
                    </span>
                  </label>
                ))}
              </div>
            )}

            {unmatched.length > 0 && (
              <div className="space-y-1.5" data-testid="location-backlog-unmatched">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  No unambiguous match — edit these on the order
                </p>
                {unmatched.slice(0, 40).map((row) => (
                  <p key={row.order_id} className="flex items-start gap-2 text-xs text-gray-600">
                    <MapPinOff className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
                    <span>
                      <span className="font-medium text-gray-800">{row.order_number}</span>{' '}
                      {row.client_name} · {shortDate(row.delivery_date)} ·{' '}
                      {row.delivery_address || 'no address on the order'}
                    </span>
                  </p>
                ))}
                {unmatched.length > 40 && (
                  <p className="text-xs text-gray-500">…and {unmatched.length - 40} more.</p>
                )}
              </div>
            )}

            {(data?.rows || []).length === 0 && (
              <p className="text-sm text-gray-600" data-testid="location-backlog-empty">
                Every order has a delivery site on file.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="location-backlog-close">
            Close
          </Button>
          <Button
            onClick={apply}
            disabled={loading || applying || chosen.size === 0}
            className="bg-amber-600 hover:bg-amber-700"
            data-testid="location-backlog-apply"
          >
            {applying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Apply {chosen.size} match{chosen.size === 1 ? '' : 'es'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LocationBacklogDialog;
